import * as lodash from 'lodash'
const _ = lodash.transform ? lodash : lodash.default
import { UOMByName } from '../types/UnitOfMeasure'
import { LookupTable, UnitOfMeasure } from '../types/dtos'
import { LookupTableByName } from '../types/LookupTable'
import { Quantifier, UnitValue } from '../types/quantifier'
import UnitConvert, { unitvalue } from '../analysis/UnitConvert'

const PLUS_MINUS = '\xB1'

const SUPER_NUMERALS = ['\u2070', '\u00B9', '\u00B2', '\u00B3', '\u2074', '\u2075', '\u2076', '\u2077', '\u2078', '\u2079']
const SUB_NUMERALS = ['\u2080', '\u2081', '\u2082', '\u2083', '\u2084', '\u2085', '\u2086', '\u2087', '\u2088', '\u2089']

const SUPER_SUB_RE = new RegExp(SUPER_NUMERALS.concat(SUB_NUMERALS).join('|'), 'gi')

export function replaceSupers(s) {
	return s.replace(SUPER_SUB_RE, (match) => {
		let i = _.indexOf(SUPER_NUMERALS, match)
		if (i == -1) {
			i = _.indexOf(SUB_NUMERALS, match)
		}
		if (i != -1) {
			return i + ''
		} else {
			return match
		}
	})
}

/**
 * deal with similar unicode symbols by indexing by uppercase but testing against insensitive lowercase
 */
const nameKey = str => replaceSupers(str.toUpperCase())

const MICRO_RE = /^[\u00B5\u03BC]/

export const unitAlias = name =>
	MICRO_RE.test(name) ? name.replace(MICRO_RE, 'micro') : undefined

export const indexUnits = (uoms: UnitOfMeasure[]): UOMByName => {
	let result = _.transform(uoms, (result, uom) => {
		for (const name of [uom.Name.Name, unitAlias(uom.Name.Name)]) {
			if (name) {
				const k = nameKey(name);
				(result[k] || (result[k] = [])).push(uom)
			}
		}
	}, {})
	return result
}

// deal with similar unicode symbols by indexing by uppercase but testing case-insensitively
export const unitsEqual = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0

export function lookupUnit(str, uomsByName: UOMByName, acceptAny = false): UnitOfMeasure {
	if (typeof str == 'string') {
		str = str.trim()
	} else {
		return
	}
	const group = uomsByName[nameKey(str)]
	if (group) {
		return group.find(unit => unitsEqual(str, unit.Name.Name)) || (acceptAny ? group[0] : undefined) // source may have been converted to 'micro'
	}
}

export function parseEqualityOps(records: LookupTable[]): LookupTableByName {
	const byNames = {} as any
	for (const rec of records) {
		if (!rec.Name || !Number.isNaN(Number(rec.Name.replace(/,/, '')))) continue // filter out numeric equality operators
		const name = rec.Name
		const values = byNames[name] || (byNames[name] = [])
		values.push(rec)
		for (const binding of rec.Bindings || []) {
			if (binding.Name != name) {
				(byNames[binding.Name] || (byNames[binding.Name] = [])).push(rec)
			}
		}
	}
	return _.mapValues(byNames, (alternatives, binding) =>
	  // if multiple records are bound similarly, prefer the one whose name does NOT match the binding
	  alternatives.find(rec => rec.Name != binding) || alternatives[0]
	)
}

const operatorsEqual = (a, b, eqOps: LookupTableByName = {}) =>
	((eqOps[a]?.Name) || a || '=') == ((eqOps[b]?.Name) || b || '=')


const EPSILON = 0.0000001

function valuesEqual(unitvalues: unitvalue[]) {
	const normalized = UnitConvert.normalizeValues(unitvalues)
	const n0 = Number(normalized[0].number)
	return !normalized.hasUnconvertableValues && normalized.every(({ number, unit }) =>
		(number == n0 || Math.abs(Number(number) - n0) < EPSILON) && unit == normalized[0].unit
	)
}

const unitValuesEqual = (uva: UnitValue, uvb: UnitValue, eqOps: LookupTableByName) =>
	operatorsEqual(uva.EqualityOperator, uvb.EqualityOperator, eqOps) &&
		!!uva.SD == !!uvb.SD &&
		valuesEqual([uva, uvb].map(uv => ({
			unit: uv.Unit.Name.Name,
			number: _.defaultTo(uv.Number, 0) * _.defaultTo(uv.Multiplier, 1)
		}))) &&
		(uva.SD ? valuesEqual([uva, uvb].map(uv => ({
					unit: uv.Unit.Name.Name,
					number: _.defaultTo(uv.SD, 0)
				})))
				: true
		)

const boundsEqual = (aunits: UnitValue[], bunits: UnitValue[], eqOps: LookupTableByName) =>
	aunits?.length == bunits?.length &&
		// convert each part of each bound and ensure the normalized values are equal
		(aunits || []).every((uva, i) => unitValuesEqual(uva, bunits[i], eqOps))

type Dosage = any

/**
 * Compare pair of list of Quantifiers, performing unit conversion/value normalization
 */
export const compareQuantifiers = ({ a, b }: { a: Quantifier[], b: Quantifier[] }, eqOps: LookupTableByName) =>
	_.zip(a, b).map(([a, b]) =>
		['Low', 'High'].every(bound => (!!a == !!b) && boundsEqual(a[bound]?.Units, b[bound]?.Units, eqOps)))

const cardinalitiesEqual = (arrayA = [], arrayB = [], maxDepth = Infinity, subst = a => a, depth = 0) => {
	if (!Array.isArray(arrayA) || !Array.isArray(arrayB)) return false
	if (arrayA.length == 0 && arrayB.length == 0) return true
	if (depth < maxDepth) {
		arrayA = subst(arrayA)
		arrayB = subst(arrayB)
		const nextDepth = depth + 1
		return arrayA.length == arrayB.length && arrayA.every((a, i)  => cardinalitiesEqual(a, arrayB[i], maxDepth, subst, nextDepth))
	} else {
		return arrayA.length == arrayB.length
	}
}

/**
 * Compare two groups' [interventions[treatments[dosages]] performing unit conversion/value normalization
 * Number and Type of Dosage must match, and value/SD must be equivalent after unit-conversion
 */
export async function compareGroupDosages(itvnDosagesA: Dosage[][][], itvnDosagesB: Dosage[][][], compareDosages) {
	if (itvnDosagesA.length != itvnDosagesB.length)
		/* different # of interventions */
		return false

	const emptyDosages = [[]]
	const dosages = arr => arr.length == 0 ? emptyDosages : arr
	// normalize empty dosages
	itvnDosagesA = itvnDosagesA.map(itvnDosages => dosages(itvnDosages))
	itvnDosagesB = itvnDosagesB.map(itvnDosages => dosages(itvnDosages))

	if (!cardinalitiesEqual(itvnDosagesA, itvnDosagesB, 2)) return false
	if (itvnDosagesA.length == 0 || itvnDosagesA.every(t => t.every(d => d.length == 0))) {
		// all empty/no dosages
		return true
	}
	// same number of treatments and dosages, so compare all the quantifiers/ranges
	const result = await compareDosages({
		a: itvnDosagesA.flat(3).map(fromDosage),
		b: itvnDosagesB.flat(3).map(fromDosage)
	})
	return result.every(b => b)
}

export const isNAUnit = u => ['NA', 'N/A'].includes(u.toUpperCase())

export const UNIT_HAS_NAME = u => u.Name.Name?.length

export function validateQuantifier(quantifier: Quantifier, allowSD = true, requireValidUnits = true, acceptUnit = UNIT_HAS_NAME) {
	const units = [quantifier.Low, quantifier.High].flatMap(p => p ? p.Units : [])
	if (!allowSD && units.some(u => u.SD != undefined)) return false
	if (requireValidUnits && !units.length) return false
	if (requireValidUnits || acceptUnit) {
		return units.every(u =>
			(!requireValidUnits || ((u.Unit && u.Unit['Id'] != null))) &&
			(!acceptUnit || acceptUnit(u.Unit))
		)
	}
	return true
}

/**
 * Convert known Dosage types to equivalent Quantifier forms
 */
export function fromDosage(dosage) {
	let q: Quantifier
	const unit = (Name: string) => ({ Name: { Name } })
	const singleBound = ({Number, SD, Unit}, unt?, dfltUnit?) => ({
		Units: [{
			Number,
			Unit: unit(unt || Unit || dfltUnit),
			SD
		}]
	})
	switch (dosage.Type) {
		case 'Fixed':
		case 'Mean':
			const bound = singleBound(dosage, dosage.Unit)
			q = {
				Gestalt: boundsToString(bound.Units),
				High: bound
			}
			break
		case 'Flex':
		case 'UnitsRange':
			// if NA unit was explicitly stored, try to determine best unit after the fact
			// let dosageUnit = dosage.Unit
			// if (isNAUnit(dosage.Unit)) {
			// 	dosageUnit = _.compact([dosage.High, dosage.Low]).find(bound => !isNAUnit(bound.Unit))?.Unit || dosage.Unit
			// }
			const low = dosage.Low && singleBound(dosage.Low, undefined, dosage.Unit)
			const high = dosage.High && singleBound(dosage.High, undefined, dosage.Unit)
			q = {
				Gestalt: boundsToString(low?.Units, high?.Units),
				Low: low,
				High: high
			}
			break
	}
	if (!q) console.warn('Unhandled Dosage Type: ' + dosage.Type)
	return q
}

export const toRangePart = (units = []) => ({
	// just use the numeric value of the first unit, disregard the rest
	Number: _.defaultTo(units[0]?.Number, 0) * _.defaultTo(units[0]?.Multiplier, 1),
	// join any distinct units into compound/multi-part unit
	Unit: units.map(u => u.Unit.Name.Name).join('/'),
	SD: units[0]?.SD
})

/**
 * Convert Quantifier to Dosage, inferring type from presence of bounds and SD field; coalesce multiple units
 */
export function toDosage(quantifier: Quantifier) {
	if (!quantifier) return
	if (quantifier?.Low) {
		// multiple bounds, construct a UnitsRange type
		const low = toRangePart(quantifier.Low?.Units)
		const high = toRangePart(quantifier.High?.Units)
		// supports single (possibly multi-part) unit for both bounds
		const unit = _.compact([high.Unit, low.Unit]).find(u => !!u?.Name?.Name && !isNAUnit(u.Name.Name.toUpperCase())) || high.Unit || low.Unit
		return {
			Type: 'UnitsRange',
			// synthesize single unit-value multi-unit bounds
			Low: low,
			High: high,
			Unit: unit
		}
	} else {
		// single bound, construct Fixed type
		const range = toRangePart(quantifier.High?.Units)
		return {
			Type: range.SD ? 'Mean' : 'Fixed',
			// Dosages only support a single unit/value part, do our best to sythesize one
			...range,
		}
	}
	// no other types supported.
}

function unitsToString(units = []) {
	let parts = []
	for (const unit of units) {
		const isna = !unit.Unit || !unit.Unit.Name?.Name || isNAUnit(unit.Unit?.Name.Name)
		if (unit.Number == 1 && !unit.SD && parts.length && isna) {
			continue
		}
		const preceding = ((unit.Number != null && (!parts.length || unit.Number != 1)) ? unit.Number : '') +
						  (unit.SD != null ? `${PLUS_MINUS}${unit.SD}` : '')
		parts.push(preceding + (isna ? '' : ((preceding ? ' ' : '') + (unit.Unit?.Name?.Name || ''))))
	}
	return parts.filter(p => !!p).join('/')
}

const boundsToString = (...bounds: UnitValue[][]) =>
	bounds.filter(b => !!b).map(units => unitsToString(units)).join(' - ')

export const toString = (quantifier: Quantifier) =>
	boundsToString(quantifier?.Low?.Units, quantifier?.High?.Units)